<script setup lang="ts">
import { DsBadge, DsIcon, NotificationVariant, useNotification } from '@demvsystems/design-components';
import { ref, computed, watch } from 'vue';
import { useHandlungStore } from '@/handlungen/store/handlungStore.ts';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router'
import type { Handlung }from '@/api/handlungen/types';
import HeadlineBlock from '@/handlungen/components/HeadlineBlock.vue';
import TextBlock from '@/handlungen/components/TextBlock.vue';
import ExternalLinkBlock from '@/handlungen/components/ExternalLinkBlock.vue';
import ChoiceBlock from '@/handlungen/components/ChoiceBlock.vue';
import SubmitBlock from '@/handlungen/components/SubmitBlock.vue';
import DeeplinkBlock from '@/handlungen/components/DeeplinkBlock.vue';
import HandlungDetailSkeleton from '@/handlungen/components/HandlungDetailSkeleton.vue';
import ShallowCard from '@/contracts/components/ShallowCard.vue';
import { call } from '@/api/lib/integration';
import { logHandlung, submitHandlung } from '@/api/handlungen/handlungenApi.ts';
import { captureException } from '@sentry/vue';
import { Finanzmanager } from '@/application/router/types/types.ts';
import { ForbiddenError } from "@/api/lib/errors.ts";
import PageContentWrapper from '@/application/layouts/ContentWrapper/PageContentWrapper.vue';
import ContractSuggestionBlock from '@/handlungen/components/ContractSuggestionBlock.vue';
import BankAccountSuggestionBlock from '@/handlungen/components/BankAccountSuggestionBlock.vue';

const componentMap = {
  headline: HeadlineBlock,
  text: TextBlock,
  externalLink: ExternalLinkBlock,
  choice: ChoiceBlock,
  submit: SubmitBlock,
  deeplink: DeeplinkBlock,
  contractSuggestion: ContractSuggestionBlock,
  bankAccountSuggestion: BankAccountSuggestionBlock,
} as const;
const router = useRouter();
const { id } = defineProps<{ id: number }>();
const { initializing } = storeToRefs(useHandlungStore());
const handlung = computed<null|Handlung>(() => useHandlungStore().getHandlungById(id));
const modelValues = ref<Record<string, string>>({});

watch(
  [initializing, handlung],
  () => !initializing.value && !handlung.value && router?.push({ name: Finanzmanager.HANDLUNGEN }),
    { immediate: true },
);

function toKeyValuePairs(obj: Record<string, string>): Array<{ id: string; selected_id: string }> {
  return Object.entries(obj).map(([id, selected_id]) => ({ id, selected_id }));
}

async function submit(submitId: number) {
  await logHandlung({
    handlungId: id,
    context: 'submit',
    data: modelValues.value,
  });
  await call(submitHandlung({
      handlungId: id,
      submit_id: submitId,
      bausteine: toKeyValuePairs(modelValues.value),
    }),
    (data) => {
      useHandlungStore().updateHandlung(data);
    },
    (error) => {
      useNotification().show(
        error instanceof ForbiddenError
          ? error.message
          : 'Ein unerwarteter Fehler ist aufgetreten',
        NotificationVariant.Error,
      );
      captureException(error);
    },
  )
}
</script>

<template>
  <PageContentWrapper>
    <div class="-mx-4 md:mx-0">
      <ShallowCard
        class="md:w-2/3 lg:w-1/2"
        no-hover
      >
        <HandlungDetailSkeleton v-if="initializing || !handlung" />
        <div
          v-else-if="handlung.interaction_status === 'open'"
          class="flex flex-col grow gap-1"
        >
          <div class="flex flex-col items-start mb-2">
            <h3 class="text-gray-900 mb-0">{{ handlung.title }}</h3>
            <span v-if="handlung.caption" class="text-gray-700">{{ handlung.caption }}</span>
            <DsBadge v-if="handlung.occasion" class="mt-2">{{ handlung.occasion }}</DsBadge>
          </div>
          <component
            :is="componentMap[baustein.type]"
            v-for="baustein in handlung.bausteine"
            :key="baustein.id"
            :handlung-id="handlung.id"
            :baustein="baustein"
            :model-value="modelValues[baustein.id]"
            @update:model-value="value => modelValues[baustein.id] = value"
            @submit="(value) => submit(value)"
          />
        </div>
        <div
          v-else-if="handlung.interaction_status === 'done'"
          class="flex flex-row gap-4 items-center py-6 sm:py-6 md:px-2"
        >
          <div
            class="bg-green-100 text-green-700 mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-12 sm:w-12"
          >
            <DsIcon
              name="check"
              size="lg"
              variant="regular"
              fixed-width
            />
          </div>
          <p class="my-2">
            Die Handlung <span class="font-semibold text-gray-900">{{ handlung.title }}</span> wurde abgeschlossen und ist
            nicht mehr verfügbar.
          </p>
        </div>
      </ShallowCard>
    </div>
  </PageContentWrapper>
</template>

<style scoped>
::v-deep(h3) {
  margin-bottom: 0;
}
</style>
